import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { bookingActions } from './slice';
dayjs.extend(utc);
dayjs.extend(timezone);

// 🔥 Get all bookings
export function* getBookings() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Fetch all bookings
		const bookings = yield call(() => SuperFetch.get('/booking'));

		if (bookings.statusCode === 200) {
			yield put(bookingActions.loadBookingsSuccess({ orgID, data: bookings.data }));
		} else {
			throw Error(bookings.error.message || bookings.error || 'Failed to load bookings.');
		}
	} catch (error) {
		yield put(bookingActions.loadBookingsError(error));
	}
}

// 🔥 Create a booking
export function* createBooking({ payload }) {
	try {
		const { data } = payload;
		const createBookingRes = yield call(() => SuperFetch.post('/booking', { ...data, timezone: dayjs.tz.guess() }));

		if (createBookingRes.statusCode === 200) {
			Notification('success', 'Booking Created Successfully!');
			yield put(bookingActions.createBookingSuccess());
		} else {
			Notification('error', 'Failed to create booking.');
			throw Error(
				createBookingRes.error.message ||
					createBookingRes.error ||
					'Failed to create booking. Please try again.'
			);
		}
	} catch (error) {
		yield put(bookingActions.createBookingError(error));
		Notification('error', error.message || 'Failed to create booking. Please try again.');
	}
}

// 🔥 After creating a booking, reload the list
export function* createBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// 🔥 Update a booking
export function* updateBooking({ payload }) {
	try {
		const { bookingID, data } = payload;
		const updateBookingRes = yield call(() => SuperFetch.patch(`/booking/${bookingID}`, data));

		if (updateBookingRes.statusCode === 200) {
			Notification('success', 'Booking updated successfully.');
			yield put(bookingActions.updateBookingSuccess());
		} else {
			Notification('error', 'Failed to update booking.');
			throw Error(updateBookingRes.error.message || updateBookingRes.error || 'Failed to update booking.');
		}
	} catch (error) {
		yield put(bookingActions.updateBookingError(error));
		Notification('error', error.message || 'Something went wrong while updating booking.');
	}
}

// 🔥 After updating a booking, reload the list
export function* updateBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// 🔥 Delete a booking
export function* deleteBooking({ payload }) {
	try {
		const { bookingID } = payload;
		const deleteBookingRes = yield call(() => SuperFetch.delete(`/booking/${bookingID}`));

		if (deleteBookingRes.statusCode === 200) {
			Notification('success', 'Booking has been permanently deleted.');
			yield put(bookingActions.deleteBookingSuccess());
		} else {
			Notification('error', 'Failed to delete booking.');
			throw Error(
				deleteBookingRes.error.message || deleteBookingRes.error || 'Failed to delete. Please try again.'
			);
		}
	} catch (error) {
		yield put(bookingActions.deleteBookingError(error));
		Notification('error', error.message || 'Failed to delete. Please try again.');
	}
}

// 🔥 After deleting a booking, reload the list
export function* deleteBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// ─── Room/Space booking sagas (v5 /booking/* room endpoints) ───────────────────

// 🔥 Load all room bookings (GET /booking/rooms) — the operator table source
export function* getRoomBookings({ payload } = {}) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const query = {};
		if (payload?.siteID) query.siteID = payload.siteID;
		if (payload?.status) query.status = payload.status;
		const res = yield call(() => SuperFetch.get('/booking/rooms', query));
		if (res.statusCode === 200) {
			yield put(bookingActions.loadRoomBookingsSuccess({ orgID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load room bookings.');
		}
	} catch (error) {
		yield put(bookingActions.loadRoomBookingsError(error));
	}
}

// 🔥 Load the pending approval queue (GET /booking/pending)
export function* getPendingBookings() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get('/booking/pending'));
		if (res.statusCode === 200) {
			yield put(bookingActions.loadPendingBookingsSuccess({ orgID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load pending bookings.');
		}
	} catch (error) {
		yield put(bookingActions.loadPendingBookingsError(error));
	}
}

// 🔥 Create a room booking (POST /booking/resource)
export function* createRoomBooking({ payload }) {
	try {
		const res = yield call(() =>
			SuperFetch.post('/booking/resource', { ...payload, timezone: payload.timezone || dayjs.tz.guess() })
		);
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', `Booking created (${res.data?.status || 'ok'}).`);
			yield put(bookingActions.createRoomBookingSuccess(res.data || null));
		} else if (res.statusCode === 409) {
			// A recurring series with a conflicting occurrence is all-or-nothing: the backend created
			// nothing and returns the clashing windows. Distinguish it from a single-slot clash.
			const conflicts = res.data?.conflicts;
			const msg = conflicts?.length
				? `${conflicts.length} occurrence(s) conflict with existing bookings — nothing was created.`
				: 'That time slot is no longer available.';
			Notification('error', msg);
			throw Error('Conflict');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to create booking.');
		}
	} catch (error) {
		yield put(bookingActions.createRoomBookingError(error));
		if (error.message !== 'Conflict') Notification('error', error.message || 'Failed to create booking.');
	}
}

// 🔥 Approve / reject / cancel a room booking
export function* approveBooking({ payload }) {
	try {
		const { bookingID, assignedToPerson } = payload;
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/approve`, { assignedToPerson }));
		if (res.statusCode === 200) {
			Notification('success', 'Booking approved.');
			yield put(bookingActions.roomActionSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to approve booking.');
		}
	} catch (error) {
		yield put(bookingActions.roomActionError(error));
		Notification('error', error.message || 'Failed to approve booking.');
	}
}

export function* rejectBooking({ payload }) {
	try {
		const { bookingID, rejectionReason } = payload;
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/reject`, { rejectionReason }));
		if (res.statusCode === 200) {
			Notification('success', 'Booking rejected.');
			yield put(bookingActions.roomActionSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to reject booking.');
		}
	} catch (error) {
		yield put(bookingActions.roomActionError(error));
		Notification('error', error.message || 'Failed to reject booking.');
	}
}

export function* cancelRoomBooking({ payload }) {
	try {
		const { bookingID, scope } = payload;
		// scope ('this' | 'following' | 'all') is sent only for a recurring-series cancel; the server
		// treats a non-recurring booking (or no scope) as a plain single cancel.
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/cancel`, scope ? { scope } : {}));
		if (res.statusCode === 200) {
			Notification('success', 'Booking cancelled.');
			yield put(bookingActions.roomActionSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to cancel booking.');
		}
	} catch (error) {
		yield put(bookingActions.roomActionError(error));
		Notification('error', error.message || 'Failed to cancel booking.');
	}
}

// Reload the room list only once NO drag is still optimistically applied — whichever handler (success
// or error) removes the FINAL in-flight snapshot owns the authoritative reload, so a wholesale roomData
// replace can't snap a concurrently-moved booking back to its old slot mid-flight. Reload brings in the
// server-authoritative window + any re-provisioned access (new cloud-key URL).
function* reloadRoomsIfSettled() {
	const stillPending = yield select((s) => Object.keys(s.Bookings.rescheduleSnapshots || {}).length);
	if (!stillPending) yield put(bookingActions.loadRoomBookings());
}

// 🔥 Reschedule a room booking (PATCH /booking/:id/reschedule) — drag-to-move / resize / cross-resource.
// The slice has already applied the move optimistically; on failure we roll it back.
export function* rescheduleBooking({ payload }) {
	const { orgID, bookingID, startTime, endTime, resourceID, scope } = payload;
	try {
		const body = { startTime, endTime };
		if (resourceID != null) body.resourceID = resourceID;
		if (scope) body.scope = scope; // 'occurrence' → move a single occurrence of a recurring series
		const res = yield call(() => SuperFetch.patch(`/booking/${bookingID}/reschedule`, body));
		if (res.statusCode === 200) {
			yield put(bookingActions.rescheduleBookingSuccess({ bookingID }));
			yield* reloadRoomsIfSettled();
		} else if (res.statusCode === 409) {
			Notification('error', res.message || 'That time is no longer available.');
			throw Error('conflict');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to move booking.');
		}
	} catch (error) {
		yield put(bookingActions.rescheduleBookingError({ orgID, bookingID, error: String(error.message || error) }));
		if (error.message !== 'conflict') Notification('error', error.message || 'Failed to move booking.');
		yield* reloadRoomsIfSettled();
	}
}

// 🔥 Booking Log — load recent booking domain events (audit trail)
export function* getBookingEvents({ payload = {} }) {
	try {
		const res = yield call(() => SuperFetch.get('/booking/events', payload));
		if (res.statusCode === 200) {
			yield put(bookingActions.loadBookingEventsSuccess({ data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load booking log.');
		}
	} catch (error) {
		yield put(bookingActions.loadBookingEventsError(error));
	}
}

// 🔥 Mark a booking as a no-show (revokes access; manager-gated server-side).
export function* markNoShow({ payload }) {
	try {
		const { bookingID, reason } = payload;
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/no-show`, { reason: reason || null }));
		if (res.statusCode === 200) {
			Notification('success', 'Booking marked as a no-show.');
			yield put(bookingActions.roomActionSuccess());
		} else if (res.statusCode === 409) {
			Notification('warning', res.message || 'Only an approved booking past its start can be a no-show.');
			throw Error('blocked');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to mark no-show.');
		}
	} catch (error) {
		yield put(bookingActions.roomActionError(error));
		if (error.message !== 'blocked') Notification('error', error.message || 'Failed to mark no-show.');
	}
}

// 🔥 Check a booking in (operator). Manager-gated server-side; reloads the list so the Checked-in badge shows.
export function* checkInBooking({ payload }) {
	try {
		const { bookingID } = payload;
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/check-in`, {}));
		if (res.statusCode === 200) {
			Notification('success', 'Booking checked in.');
			yield put(bookingActions.roomActionSuccess());
		} else if (res.statusCode === 409) {
			Notification('warning', res.message || 'This booking can no longer be checked in.');
			throw Error('blocked');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to check in.');
		}
	} catch (error) {
		yield put(bookingActions.roomActionError(error));
		if (error.message !== 'blocked') Notification('error', error.message || 'Failed to check in.');
	}
}

// 🔥 Mint a booking's signed check-in token (for the guest QR). The component builds the QR URL from it.
export function* getCheckinToken({ payload }) {
	try {
		const { bookingID } = payload;
		const res = yield call(() => SuperFetch.get(`/booking/${bookingID}/checkin-token`));
		if (res.statusCode === 200) {
			yield put(bookingActions.loadCheckinTokenSuccess(res.data?.token || null));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to get the check-in code.');
		}
	} catch (error) {
		yield put(bookingActions.loadCheckinTokenError(error));
		Notification('error', error.message || 'Failed to get the check-in code.');
	}
}

// 🔥 Send a booking confirmation email (+ ICS attachment). `to` optional (defaults server-side to the
// booking's guest email; the dashboard passes the person's email for person bookings).
export function* sendBookingConfirmation({ payload }) {
	try {
		const { bookingID, to } = payload;
		const res = yield call(() => SuperFetch.post(`/booking/${bookingID}/send-confirmation`, to ? { to } : {}));
		if (res.statusCode === 200) {
			Notification('success', 'Confirmation email sent.');
			yield put(bookingActions.sendBookingConfirmationSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to send confirmation.');
		}
	} catch (error) {
		yield put(bookingActions.sendBookingConfirmationError(error));
		Notification('error', error.message || 'Failed to send confirmation.');
	}
}

// 🔥 After any room action, refresh the pending queue + the room bookings list
export function* roomActionSuccess() {
	yield put(bookingActions.loadPendingBookings());
	yield put(bookingActions.loadRoomBookings());
}

// 🔥 Export the saga watchers
export default function* bookingsSaga() {
	yield takeLatest(bookingActions.loadBookings, getBookings);
	yield takeLatest(bookingActions.createBooking, createBooking);
	yield takeLatest(bookingActions.createBookingSuccess, createBookingSuccess);
	yield takeLatest(bookingActions.updateBooking, updateBooking);
	yield takeLatest(bookingActions.updateBookingSuccess, updateBookingSuccess);
	yield takeLatest(bookingActions.deleteBooking, deleteBooking);
	yield takeLatest(bookingActions.deleteBookingSuccess, deleteBookingSuccess);
	// Room/Space
	yield takeLatest(bookingActions.loadRoomBookings, getRoomBookings);
	yield takeLatest(bookingActions.loadPendingBookings, getPendingBookings);
	yield takeLatest(bookingActions.createRoomBooking, createRoomBooking);
	yield takeLatest(bookingActions.createRoomBookingSuccess, roomActionSuccess);
	yield takeLatest(bookingActions.approveBooking, approveBooking);
	yield takeLatest(bookingActions.rejectBooking, rejectBooking);
	yield takeLatest(bookingActions.cancelRoomBooking, cancelRoomBooking);
	yield takeLatest(bookingActions.markNoShow, markNoShow);
	yield takeLatest(bookingActions.checkInBooking, checkInBooking);
	yield takeLatest(bookingActions.loadCheckinToken, getCheckinToken);
	// takeEvery (not takeLatest): each drag targets a distinct booking — a second move must not cancel
	// the first booking's in-flight reschedule (which would strand its optimistic patch).
	yield takeEvery(bookingActions.rescheduleBooking, rescheduleBooking);
	yield takeLatest(bookingActions.loadBookingEvents, getBookingEvents);
	yield takeLatest(bookingActions.sendBookingConfirmation, sendBookingConfirmation);
	yield takeLatest(bookingActions.roomActionSuccess, roomActionSuccess);
}
